import { Button, Form, Input } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { FiActivity, FiClock, FiUsers } from "react-icons/fi";

const EMPTY_ARRAY = [];

export const cleanStringArray = (value) =>
  (Array.isArray(value) ? value : EMPTY_ARRAY)
    .map((item) => String(item || "").trim())
    .filter(Boolean);

export const flattenLabTestLookups = (payload) => {
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  const flattened = [];

  rows.forEach((departmentEntry) => {
    if (Array.isArray(departmentEntry?.testGroups)) {
      departmentEntry.testGroups.forEach((groupEntry) => {
        (Array.isArray(groupEntry?.tests) ? groupEntry.tests : []).forEach((test) => {
          flattened.push(test);
        });
      });
      return;
    }

    if (departmentEntry?.parameterName) flattened.push(departmentEntry);
  });

  return flattened.map((test) => ({
    ...test,
    key: String(test?._id || `${test?.department}-${test?.testGroup}-${test?.parameterName}`),
  }));
};

export const toFormValues = (record = {}) => ({
  department: record.department || "",
  testGroup: record.testGroup || "",
  parameterName: record.parameterName || "",
  unit: record.unit || "",
  sampleType: record.sampleType || "",
  dataType: record.dataType || "",
  patientContent: {
    basicDescription: record.patientContent?.basicDescription || "",
    whyBooked: cleanStringArray(record.patientContent?.whyBooked),
    whoShouldTake: cleanStringArray(record.patientContent?.whoShouldTake),
    ageGenderSuitability: {
      age: record.patientContent?.ageGenderSuitability?.age || "",
      gender: record.patientContent?.ageGenderSuitability?.gender || "",
    },
  },
});

export const toPayload = (values) => {
  const patientContent = values.patientContent || {};
  const suitability = patientContent.ageGenderSuitability || {};

  return {
    department: String(values.department || "").trim(),
    testGroup: String(values.testGroup || "").trim(),
    parameterName: String(values.parameterName || "").trim(),
    unit: String(values.unit || "").trim(),
    sampleType: String(values.sampleType || "").trim(),
    dataType: String(values.dataType || "").trim(),
    patientContent: {
      basicDescription: String(patientContent.basicDescription || "").trim(),
      whyBooked: cleanStringArray(patientContent.whyBooked),
      whoShouldTake: cleanStringArray(patientContent.whoShouldTake),
      ageGenderSuitability: {
        age: String(suitability.age || "").trim(),
        gender: String(suitability.gender || "").trim(),
      },
    },
  };
};

export const displayValue = (value) =>
  value === null || value === undefined || value === "" ? "N/A" : value;

export const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const DetailRow = ({ label, children, className = "" }) => (
  <div className={`min-w-0 rounded-md border border-[#E7EBF4] bg-[#FBFCFE] p-3.5 ${className}`}>
    <p className="mb-1.5 text-[11px] font-poppins font-medium uppercase tracking-[0.04em] text-[#8A91A8]">
      {label}
    </p>
    <div className="min-w-0 whitespace-pre-wrap break-words font-poppins text-[13px] leading-5 text-[#374151]">
      {children}
    </div>
  </div>
);

const DetailSectionHeader = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 border-b border-[#E7EBF4] px-5 py-4 sm:px-6">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EEF0FF] text-[#4951EC]">
      <Icon size={18} strokeWidth={2} />
    </div>
    <div>
      <h3 className="text-[15px] font-poppins font-semibold text-[#1F2937]">{title}</h3>
      <p className="mt-0.5 text-[12px] text-[#8A91A8]">{description}</p>
    </div>
  </div>
);

const DetailCard = ({ title, description, icon, children, className = "" }) => (
  <section className={`overflow-hidden rounded-2xl border border-[#E4E8F1] bg-white shadow-[0_8px_24px_rgba(31,41,55,0.04)] ${className}`}>
    <DetailSectionHeader icon={icon} title={title} description={description} />
    <div className="p-5 sm:p-6">{children}</div>
  </section>
);

const StringList = ({ values }) => {
  const items = cleanStringArray(values);
  if (!items.length) return <span className="text-gray-400">N/A</span>;

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={`${item}-${index}`} className="flex items-start gap-2.5 rounded-md border border-[#E7EBF4] bg-white px-3 py-2.5">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#EEF0FF] text-[10px] font-semibold text-[#4951EC]">
            {index + 1}
          </span>
          <span className="min-w-0 text-[13px] leading-5 text-[#4B5563]">{item}</span>
        </div>
      ))}
    </div>
  );
};

export const LabTestDetails = ({ record }) => {
  const content = record?.patientContent || {};
  const suitability = content.ageGenderSuitability || {};

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      <DetailCard
        icon={FiActivity}
        title="Test information"
        description="Core identifiers and reporting details for this lab test."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <DetailRow label="Parameter name">{displayValue(record?.parameterName)}</DetailRow>
          <DetailRow label="Department">{displayValue(record?.department)}</DetailRow>
          <DetailRow label="Test group">{displayValue(record?.testGroup)}</DetailRow>
          <DetailRow label="Sample type">{displayValue(record?.sampleType)}</DetailRow>
          <DetailRow label="Unit">{displayValue(record?.unit)}</DetailRow>
          <DetailRow label="Data type">{displayValue(record?.dataType)}</DetailRow>
        </div>
      </DetailCard>

      <DetailCard
        icon={FiUsers}
        title="Patient-facing content"
        description="Information shown to help patients and care teams understand the test."
      >
        <div className="space-y-5">
          <DetailRow label="Basic description">{displayValue(content.basicDescription)}</DetailRow>
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <div>
              <h4 className="mb-2 text-[13px] font-poppins font-semibold text-[#374151]">Why this test is booked</h4>
              <StringList values={content.whyBooked} />
            </div>
            <div>
              <h4 className="mb-2 text-[13px] font-poppins font-semibold text-[#374151]">Who should take this test</h4>
              <StringList values={content.whoShouldTake} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailRow label="Age suitability">{displayValue(suitability.age)}</DetailRow>
            <DetailRow label="Gender suitability">{displayValue(suitability.gender)}</DetailRow>
          </div>
        </div>
      </DetailCard>

      <DetailCard
        icon={FiClock}
        title="Record metadata"
        description="Read-only information maintained by the system."
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <DetailRow label="ID"><span className="break-all">{displayValue(record?._id)}</span></DetailRow>
          <DetailRow label="Created">{formatDate(record?.createdAt)}</DetailRow>
          <DetailRow label="Last updated">{formatDate(record?.updatedAt)}</DetailRow>
        </div>
      </DetailCard>
    </div>
  );
};

export const StringListEditor = ({ name, label, addLabel }) => (
  <Form.Item label={label} className="mb-0">
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <div className="space-y-2">
          {fields.length === 0 && (
            <div className="rounded-lg border border-dashed border-[#D9DEEB] bg-white px-3 py-2 text-[12px] text-[#8A91A8]">
              No entries added yet.
            </div>
          )}
          {fields.map(({ key, ...fieldProps }) => (
            <div key={key} className="relative w-full min-w-0">
              <Form.Item
                {...fieldProps}
                className="mb-0 min-w-0"
                rules={[{ required: true, whitespace: true, message: "Enter a value or remove this row." }]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  className="!rounded-md !border-[#D9DEEB] !bg-white !py-2.5 !pr-12 text-[13px] leading-5"
                />
              </Form.Item>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label={`Remove ${label.toLowerCase()} item`}
                className="absolute right-2 top-2 h-8 w-8 rounded-md border border-red-100 bg-white text-red-500 shadow-sm hover:!border-red-200 hover:!bg-red-50"
                onClick={() => remove(fieldProps.name)}
              />
            </div>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            className="h-9 rounded-md border-[#C9D0FF] px-3 text-[12px] text-[#4951EC] hover:!border-[#4951EC] hover:!text-[#3942D4]"
            onClick={() => add("")}
          >
            {addLabel}
          </Button>
        </div>
      )}
    </Form.List>
  </Form.Item>
);

const EditSectionHeader = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 border-b border-[#E7EBF4] px-5 py-4 sm:px-6">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EEF0FF] text-[#4951EC]">
      <Icon size={18} strokeWidth={2} />
    </div>
    <div>
      <h3 className="text-[15px] font-poppins font-semibold text-[#1F2937]">{title}</h3>
      <p className="mt-0.5 text-[12px] text-[#8A91A8]">{description}</p>
    </div>
  </div>
);

const CORE_FIELD_CLASS =
  "!h-10 !rounded-md !border-[#D9DEEB] !bg-white !px-3 text-[13px] transition-colors hover:!border-[#AEB8F4] focus:!border-[#4951EC]";

const REQUIRED_RULE = (label) => [
  { required: true, whitespace: true, message: `${label} is required.` },
];

export const LabTestEditFields = () => (
  <div className="space-y-6 p-4 sm:p-6 lg:p-8">
    <section className="overflow-hidden rounded-2xl border border-[#E4E8F1] bg-white shadow-[0_8px_24px_rgba(31,41,55,0.04)]">
      <EditSectionHeader
        icon={FiActivity}
        title="Test information"
        description="Core details used to identify and report this lab test."
      />
      <div className="grid grid-cols-1 gap-x-5 gap-y-1 p-5 sm:grid-cols-2 xl:grid-cols-3 sm:p-6">
        <Form.Item name="parameterName" label="Parameter name" rules={REQUIRED_RULE("Parameter name")}>
          <Input className={CORE_FIELD_CLASS} />
        </Form.Item>
        <Form.Item name="department" label="Department" rules={REQUIRED_RULE("Department")}>
          <Input className={CORE_FIELD_CLASS} />
        </Form.Item>
        <Form.Item name="testGroup" label="Test group" rules={REQUIRED_RULE("Test group")}>
          <Input className={CORE_FIELD_CLASS} />
        </Form.Item>
        <Form.Item name="sampleType" label="Sample type" rules={REQUIRED_RULE("Sample type")}>
          <Input className={CORE_FIELD_CLASS} />
        </Form.Item>
        <Form.Item name="unit" label="Unit" rules={REQUIRED_RULE("Unit")}>
          <Input className={CORE_FIELD_CLASS} />
        </Form.Item>
        <Form.Item name="dataType" label="Data type" rules={REQUIRED_RULE("Data type")}>
          <Input className={CORE_FIELD_CLASS} />
        </Form.Item>
      </div>
    </section>

    <section className="overflow-hidden rounded-2xl border border-[#E4E8F1] bg-white shadow-[0_8px_24px_rgba(31,41,55,0.04)]">
      <EditSectionHeader
        icon={FiUsers}
        title="Patient-facing content"
        description="Keep the information clear and useful for patients and care teams."
      />
      <div className="space-y-6 p-5 sm:p-6">
        <Form.Item name={["patientContent", "basicDescription"]} label="Basic description" className="mb-0">
          <Input.TextArea
            autoSize={{ minRows: 4, maxRows: 8 }}
            className="!rounded-md !border-[#D9DEEB] !bg-[#FBFCFE] !px-3 !py-2.5 text-[13px] leading-5 hover:!border-[#AEB8F4] focus:!border-[#4951EC]"
          />
        </Form.Item>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <div className="rounded-xl border border-[#E7EBF4] bg-[#F8FAFF] p-4 sm:p-5">
            <StringListEditor
              name={["patientContent", "whyBooked"]}
              label="Why this test is booked"
              addLabel="Add reason"
            />
          </div>
          <div className="rounded-xl border border-[#E7EBF4] bg-[#F8FAFF] p-4 sm:p-5">
            <StringListEditor
              name={["patientContent", "whoShouldTake"]}
              label="Who should take this test"
              addLabel="Add intended user"
            />
          </div>
        </div>

        <div className="rounded-xl border border-[#E7EBF4] bg-[#F8FAFF] p-4 sm:p-5">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-[#4951EC] shadow-sm">
              <FiUsers size={15} />
            </span>
            <h4 className="text-[13px] font-poppins font-semibold text-[#374151]">Suitability</h4>
          </div>
          <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
            <Form.Item name={["patientContent", "ageGenderSuitability", "age"]} label="Age suitability" className="mb-0">
              <Input className={CORE_FIELD_CLASS} />
            </Form.Item>
            <Form.Item name={["patientContent", "ageGenderSuitability", "gender"]} label="Gender suitability" className="mb-0">
              <Input className={CORE_FIELD_CLASS} />
            </Form.Item>
          </div>
        </div>
      </div>
    </section>
  </div>
);
